import { RevolveFeatureDefinitionModel } from '../../definition/revolve/RevolveFeatureDefinition';
import { RevolveFeatureDefinitionBuilder } from './RevolveFeatureDefinitionBuilder';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/edit/revolve 层；
 * 2. 负责定义旋转特征在编辑会话中的 UI 草稿定义；
 * 3. 该文件把旋转角度与反向状态从宿主侧输入收口回 cad-core。
 *
 * 文件职责：
 * 1. 定义旋转编辑态草稿；
 * 2. 提供默认值、prototype 恢复与命令参数回写；
 * 3. 供宿主 PropertyManager 和编辑会话统一复用。
 */

/**
 * 旋转特征编辑定义。
 */
export interface RevolveFeatureEditDefinition {
    /** 方向 1 角度。 */
    direction1Angle: number;
    /** 是否启用方向 2。 */
    direction2Enabled: boolean;
    /** 方向 2 角度。 */
    direction2Angle: number;
    /** 是否反向。 */
    directionReversed: boolean;
    /** 是否合并结果。 */
    mergeResult: boolean;
    /** 旋转切除专属：是否翻转切除侧。 */
    flipSideToCut?: boolean;
    /** 旋转切除专属：按来源实体保存需要保留的片段键。 */
    keepShapeKeys?: Record<string, string[]>;
    /** 旋转切除专属：预览分析令牌。 */
    previewToken?: string;
}

/**
 * 克隆旋转切除保留体片段键。
 *
 * @param value 原始保留体片段键。
 * @returns 克隆后的保留体片段键。
 */
function cloneKeepShapeKeys(value: Record<string, string[]> | undefined): Record<string, string[]> {
    if (!value || typeof value !== 'object') {
        return {};
    }
    return Object.fromEntries(
        Object.entries(value)
            .filter((entry): entry is [string, string[]] => (
                typeof entry[0] === 'string'
                && Array.isArray(entry[1])
            ))
            .map(([solidId, shapeKeys]) => [
                solidId,
                shapeKeys.filter((shapeKey): shapeKey is string => typeof shapeKey === 'string'),
            ]),
    );
}

/**
 * 旋转特征编辑定义工厂。
 */
export class RevolveFeatureEditDefinitionFactory {
    /**
     * 创建默认编辑定义。
     * @returns 默认编辑定义。
     */
    public static createDefault(): RevolveFeatureEditDefinition {
        return {
            direction1Angle: Math.PI * 2,
            direction2Enabled: false,
            direction2Angle: 0,
            directionReversed: false,
            mergeResult: true,
            flipSideToCut: false,
            keepShapeKeys: {},
            previewToken: undefined,
        };
    }

    /**
     * 从旋转 prototype 恢复编辑定义。
     * @param definition 旋转特征定义。
     * @returns 编辑定义。
     */
    public static restoreFromDefinition(
        definition: RevolveFeatureDefinitionModel,
    ): RevolveFeatureEditDefinition {
        const params = definition.getCommandParams();
        return {
            direction1Angle: params.direction1Angle,
            direction2Enabled: params.direction2Enabled === true,
            direction2Angle: params.direction2Angle ?? 0,
            directionReversed: false,
            mergeResult: params.mergeResult !== false,
            flipSideToCut: params.flipSideToCut === true || params.reverseCut === true,
            keepShapeKeys: cloneKeepShapeKeys(params.keepShapeKeys),
            previewToken: params.previewToken,
        };
    }

    /**
     * 把编辑定义回写为旋转特征定义。
     * @param baseParams 原始命令参数。
     * @param definition 编辑定义。
     * @returns 旋转特征定义。
     */
    public static buildFeatureDefinition(
        baseParams: RevolveParams,
        definition: RevolveFeatureEditDefinition,
    ): RevolveFeatureDefinitionModel {
        return RevolveFeatureDefinitionBuilder.buildFeatureDefinition({
            baseParams,
            definition,
        });
    }
}
